import type { PublishedCourse } from '@slideforge/shared';
import { escapeXml } from './manifests.js';

export interface HtmlOptions {
  readonly course: PublishedCourse;
  /**
   * Inline `course.json` as a script instead of fetching it. Required for the
   * plain-HTML5 target: `fetch()` against a `file://` URL is blocked by every
   * modern browser, so a course opened from a memory stick would show a blank
   * page with only a CORS message in the console.
   */
  readonly inlineCourse: boolean;
  readonly playerScriptHref: string;
  readonly courseJsonHref: string;
}

export function indexHtml(options: HtmlOptions): string {
  const { course } = options;
  const boot = options.inlineCourse
    ? `      const course = window.__SLIDEFORGE_COURSE__;`
    : `      const course = await (await fetch(${JSON.stringify(options.courseJsonHref)})).json();`;

  return `<!doctype html>
<html lang="${escapeXml(course.language)}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeXml(course.title)}</title>
<meta name="generator" content="${escapeXml(course.generator.name)} ${escapeXml(course.generator.version)}">
<style>html,body{margin:0;padding:0;height:100%;overflow:hidden}</style>
</head>
<body>
<div id="slideforge-root"></div>
${options.inlineCourse ? `<script id="slideforge-course" type="application/json">${jsonForScriptTag(course)}</script>\n<script>window.__SLIDEFORGE_COURSE__ = JSON.parse(document.getElementById('slideforge-course').textContent);</script>\n` : ''}<script src="${escapeXml(options.playerScriptHref)}"></script>
<script>
  (async function () {
    try {
${boot}
      await SlideForgePlayer.mountPlayer({
        course: course,
        container: document.getElementById('slideforge-root'),
        search: window.location.search,
      });
    } catch (error) {
      const root = document.getElementById('slideforge-root');
      root.textContent = '';
      const p = document.createElement('p');
      p.setAttribute('role', 'alert');
      p.style.padding = '2rem';
      p.style.fontFamily = 'system-ui, sans-serif';
      p.textContent = 'Kursus ini tidak dapat dimuat. / This course could not be loaded.';
      root.appendChild(p);
      throw error;
    }
  })();
</script>
</body>
</html>
`;
}

/**
 * JSON safe to place inside a `<script>` element.
 *
 * `</script>` anywhere in the data — a slide about HTML, say — would otherwise
 * close the tag early and break the page. `<!--` is escaped for the same reason.
 */
export function jsonForScriptTag(value: unknown): string {
  return JSON.stringify(value)
    .replace(/<\/(script)/gi, '<\\/$1')
    .replace(/<!--/g, '<\\!--');
}
